import type { BadgeVariant, TextTone } from '@/components/ui/display';
import type { StepperStep, StepState } from '@/components/ui/navigation';
import type { Brand } from '@/features/brandHub/brand/types';
import type { TranslationKey } from '@/i18n';
import { readOneOf } from '@/lib/readers';

/** The wizard in order: the name creates the draft, every later step fills it. */
export const BRAND_CREATE_STEPS = [
  'name',
  'brand-guideline',
  'guideline-files',
  'look',
  'visual-elements',
  'audio',
  'done',
] as const;
export type BrandCreateStep = (typeof BRAND_CREATE_STEPS)[number];

/** Steps a brand does not need to become ready. */
export const OPTIONAL_STEPS: readonly BrandCreateStep[] = ['brand-guideline', 'visual-elements', 'audio'];

/** The steps that fill the brand, shown one at a time or all on one page. */
export const MATERIAL_STEPS: readonly BrandCreateStep[] = BRAND_CREATE_STEPS.filter(
  (step) => step !== 'name' && step !== 'done',
);

/** The three materials the look step gathers on one screen. */
export const LOOK_MATERIALS = ['logos', 'colours', 'font'] as const;

/** How much each step already holds, counted by the page. */
export type StepCounts = Partial<Record<BrandCreateStep, number>>;

export const BRAND_PARAM = 'brand';
export const STEP_PARAM = 'step';
export const VIEW_PARAM = 'view';

/** The last position in the wizard, the done step. */
export const LAST_STEP_INDEX = BRAND_CREATE_STEPS.length - 1;

/** The fade-in classes the sections of the one-page view take in turn. */
export const STEP_STAGGER = ['fade-in', 'fade-in-d1', 'fade-in-d2', 'fade-in-d3'];

/** The message key of each refusal of a new brand name. */
export const NAME_REFUSALS: Record<number, TranslationKey> = {
  400: 'brandHub.brand.reservedName',
  409: 'brandHub.brand.duplicateName',
};

/** The badge the summary shows for a step that is complete or skipped. */
export const STATE_BADGE: Partial<Record<StepState, { key: TranslationKey; variant: BadgeVariant }>> = {
  complete: { key: 'brandHub.brandCreate.complete', variant: 'success' },
  skipped: { key: 'brandHub.brandCreate.skipped', variant: 'warning' },
};

export const WIZARD_VIEWS = ['steps', 'page'] as const;
export type WizardView = (typeof WIZARD_VIEWS)[number];

/** Returns the step the URL names. Without a brand it is the name step. */
export function readBrandCreateStep(params: URLSearchParams): BrandCreateStep {
  if (!params.get(BRAND_PARAM)) return 'name';
  return readOneOf(BRAND_CREATE_STEPS, params.get(STEP_PARAM), 'brand-guideline');
}

/** Returns the wizard view the URL names, or the steps view. */
export function readWizardView(params: URLSearchParams): WizardView {
  return readOneOf(WIZARD_VIEWS, params.get(VIEW_PARAM), 'steps');
}

/** Returns the position of one step in the wizard. */
export function stepIndex(step: BrandCreateStep): number {
  return BRAND_CREATE_STEPS.indexOf(step);
}

/** Returns the step after this one, or this one when it is the last. */
export function nextStep(step: BrandCreateStep): BrandCreateStep {
  return BRAND_CREATE_STEPS[stepIndex(step) + 1] ?? step;
}

/** Returns the step before this one, or this one when it is the first. */
export function previousStep(step: BrandCreateStep): BrandCreateStep {
  return BRAND_CREATE_STEPS[stepIndex(step) - 1] ?? step;
}

/** Returns the number of the three look materials that the brand has. A material counts
 *  when its list has one item or more. */
export function lookMaterialsDone(brand: Brand, logoCount: number): number {
  const held: Record<(typeof LOOK_MATERIALS)[number], boolean> = {
    logos: logoCount > 0,
    colours: brand.colours.length > 0,
    font: brand.fonts.length > 0,
  };
  return LOOK_MATERIALS.filter((material) => held[material]).length;
}

/** Returns the state of one step from the current counts. A step with content is complete.
 *  An empty step the user went past is skipped. */
export function stepState(
  step: BrandCreateStep,
  brand: Brand | null,
  counts: StepCounts,
  reachedIndex = 0,
): StepState {
  if (step === 'name') return brand ? 'complete' : 'pending';
  if (!brand) return 'locked';
  if (step === 'done') return 'pending';
  if ((counts[step] ?? 0) > 0) return 'complete';
  return stepIndex(step) < reachedIndex ? 'skipped' : 'pending';
}

/** Returns the label with the count of the step, or the label alone if the count is zero. */
export function withCount(label: string, count: number | undefined, total?: number): string {
  if (!count) return label;
  return total ? `${label} (${count}/${total})` : `${label} (${count})`;
}

/** Returns the label colour of one step in the navigation. */
export function stepTone(active: boolean, state: StepperStep['state']): TextTone {
  if (active) return 'primary';
  return state === 'skipped' ? 'muted' : 'default';
}
